import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Flame, Mail, Lock, UserRound, ShieldCheck, AlertTriangle, ArrowRight } from "lucide-react";

export default function LoginPage() {
  const navigate = useNavigate();
  const { login, register } = useAuth();
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [role, setRole] = useState("consultant");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      if (mode === "login") await login(email, password);
      else await register({ email, password, name, role });
      navigate("/home", { replace: true });
    } catch (err) {
      setError(err?.response?.data?.detail || "Bir hata oluştu");
    } finally { setLoading(false); }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/auth/callback";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="auth-shell" data-testid="auth-shell">
      <div className="auth-stage">
        <div className="auth-stripe" aria-hidden="true"/>
        <aside className="auth-pitch" data-testid="auth-pitch">
          <div className="brand"><span className="brand-mark"><Flame size={18}/></span><span>HAZMAT<span>CORE</span></span></div>
          <div className="eyebrow">YANGIN UYGUNLUK MERKEZİ</div>
          <h1>Mevzuata dayalı ön değerlendirme için güvenli giriş.</h1>
          <p>BYKHY kural paketleri, kroki üzerinde AI önerileri ve denetlenebilir rapor taslağı tek bir çalışma alanında.</p>
          <ul className="auth-features">
            <li><ShieldCheck size={15}/> Danışman ve bina sahibi görünümleri</li>
            <li><ShieldCheck size={15}/> Her bulgu için kaynak madde ve güven durumu</li>
            <li><ShieldCheck size={15}/> AI önerileri yalnızca kullanıcı doğrulamasıyla rapora alınır</li>
          </ul>
          <small className="auth-legal">Bu çalışma resmi uygunluk kararı değildir; yetkili kurum onayı gerektirir.</small>
        </aside>
        <main className="auth-card" data-testid="auth-card">
          <div className="auth-tabs" role="tablist">
            <button type="button" role="tab" aria-selected={mode === "login"} className={mode === "login" ? "active" : ""} onClick={() => setMode("login")} data-testid="login-tab">Giriş yap</button>
            <button type="button" role="tab" aria-selected={mode === "register"} className={mode === "register" ? "active" : ""} onClick={() => setMode("register")} data-testid="register-tab">Hesap oluştur</button>
          </div>
          <button type="button" className="google-button" onClick={googleLogin} data-testid="google-login-button">
            <svg width="18" height="18" viewBox="0 0 48 48"><path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.9 29.3 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3 0 5.8 1.1 7.9 3l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.2-.1-2.3-.4-3.5z"/><path fill="#FF3D00" d="M6.3 14.1l6.6 4.8C14.6 15 18.9 12 24 12c3 0 5.8 1.1 7.9 3l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.5 6.3 14.1z"/><path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.5-5.2l-6.2-5.3C29.1 35 26.7 36 24 36c-5.3 0-9.7-3.1-11.3-7.9l-6.5 5C9.6 39.4 16.2 44 24 44z"/><path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.3-2.3 4.3-4.1 5.6l6.2 5.3C40.3 36 44 30.5 44 24c0-1.2-.1-2.3-.4-3.5z"/></svg>
            Google ile devam et
          </button>
          <div className="auth-divider"><span>veya e-posta ile</span></div>
          <form onSubmit={submit} className="auth-form" data-testid="auth-form">
            {mode === "register" && (
              <>
                <label><UserRound size={14}/> Ad Soyad
                  <input type="text" required value={name} onChange={(e) => setName(e.target.value)} placeholder="Örn. Ayşe Demir" data-testid="register-name-input"/>
                </label>
                <label><ShieldCheck size={14}/> Rol
                  <select value={role} onChange={(e) => setRole(e.target.value)} data-testid="register-role-select">
                    <option value="consultant">Yangın danışmanı</option>
                    <option value="owner">Bina / tesis sahibi</option>
                    <option value="manager">İşletme yöneticisi</option>
                  </select>
                </label>
              </>
            )}
            <label><Mail size={14}/> E-posta
              <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="ornek@firma.com" data-testid="auth-email-input"/>
            </label>
            <label><Lock size={14}/> Şifre
              <input type="password" required minLength={8} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="En az 8 karakter" data-testid="auth-password-input"/>
            </label>
            {error && <div className="auth-error" data-testid="auth-error"><AlertTriangle size={14}/> {error}</div>}
            <button className="primary-btn auth-submit" type="submit" disabled={loading} data-testid="auth-submit-button">
              {loading ? "İşleniyor…" : (mode === "login" ? "Çalışma alanına gir" : "Hesabı oluştur")} <ArrowRight size={16}/>
            </button>
          </form>
          <small className="auth-foot">Girişinizle BYKHY çalışma alanınıza erişebilirsiniz. Bu platform yangın uygunluk denetim yetkisi vermez.</small>
        </main>
      </div>
    </div>
  );
}
